import type { QueryClient } from '@tanstack/svelte-query';
import {
	getPrimaryGalleryBaseParams,
	getPrimaryGalleryRestParams,
} from '../getPrimaryGalleryRestParams/getPrimaryGalleryRestParams';
import {
	getPrimaryCollectionArtworks,
	primaryCollectionArtworksQueryKey,
} from '#lib/websites/primary/api/getPrimaryCollectionPage.js';
import { PRIMARY_GALLERY_STALE_TIME } from '#lib/websites/primary/constants/gallery.js';
import type { PrimaryGalleryPageLoadEvent } from '$routes/primary/collection/[collectionSlug]/types';

export const primaryGalleryPageLoad = async ({
	parent,
	data,
	url,
	fetch,
}: PrimaryGalleryPageLoadEvent) => {
	const parentData = await parent();
	const queryClient: QueryClient = parentData.queryClient;
	const collectionSlug = data.collection.slug as string;
	const defaultSort = data.defaultSort;

	// Seeds the unfiltered first page the server load already fetched, under the
	// key the component's infinite query will look for.
	queryClient.setQueryData(
		primaryCollectionArtworksQueryKey(
			getPrimaryGalleryBaseParams({ collectionSlug, defaultSort })
		),
		{ pageParams: [0], pages: [data.artworks] }
	);

	const params = getPrimaryGalleryRestParams({
		collectionSlug,
		defaultSort,
		filterSections: data.filterSections,
		searchParams: url.searchParams,
	});

	queryClient.prefetchInfiniteQuery({
		queryKey: primaryCollectionArtworksQueryKey(params),
		queryFn: ({ pageParam }) =>
			getPrimaryCollectionArtworks(fetch, {
				...params,
				offset: pageParam as number,
			}),
		initialPageParam: 0,
		staleTime: PRIMARY_GALLERY_STALE_TIME,
	});

	// `...data` only, matching the GraphQL sibling: the parent's data reaches the
	// page through the layout, and re-spreading it here would put `queryClient`
	// into the page's own data.
	return {
		...data,
		params,
	};
};
